import { useState } from "react";
import { FiShield, FiCheck, FiAlertCircle } from "react-icons/fi";
import { useAuth } from "../../../auth/AuthProvider";
import { disable2fa } from "../../../api/auth";
import TotpSetupModal from "./TotpSetupModal";

interface Props {
    totpEnabled: boolean;
    onStatusChange: () => void;
}

export default function TwoFactorCard({ totpEnabled, onStatusChange }: Props) {
    const { token, isAdmin } = useAuth();
    const [showSetup, setShowSetup] = useState(false);
    const [disabling, setDisabling] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const handleDisable = async () => {
        setError("");
        setDisabling(true);
        try {
            await disable2fa(token!);
            setSuccess("2FA disabled");
            onStatusChange();
        } catch (err: any) {
            setError(err.message || "Failed to disable 2FA");
        } finally {
            setDisabling(false);
        }
    };

    return (
        <>
            <div className="card">
                <div className="flex items-center gap-3 mb-4">
                    <div className={`p-2 rounded-lg ${totpEnabled ? 'bg-ecosystem-500/10 text-ecosystem-400' : 'bg-surface-500 text-text-muted'}`}>
                        <FiShield className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-h4 text-text-primary">Two-Factor Authentication</h3>
                        <p className="text-caption text-text-secondary">
                            Add an extra layer of security to your account
                        </p>
                    </div>
                    {totpEnabled && (
                        <span className="px-2 py-1 rounded text-caption font-medium bg-ecosystem-500/20 text-ecosystem-400 flex items-center gap-1">
                            <FiCheck className="w-3.5 h-3.5" /> Enabled
                        </span>
                    )}
                </div>

                {error && (
                    <div className="mb-4 p-3 rounded-lg bg-health-500/10 border border-health-500/30 flex items-center gap-2">
                        <FiAlertCircle className="text-health-400 shrink-0" />
                        <p className="text-body-sm text-health-400">{error}</p>
                    </div>
                )}
                {success && (
                    <div className="mb-4 p-3 rounded-lg bg-ecosystem-500/10 border border-ecosystem-500/30 flex items-center gap-2">
                        <FiCheck className="text-ecosystem-400 shrink-0" />
                        <p className="text-body-sm text-ecosystem-400">{success}</p>
                    </div>
                )}

                {totpEnabled ? (
                    <div className="space-y-3">
                        <p className="text-body-sm text-text-secondary">
                            Your account is protected with a TOTP authenticator app.
                        </p>
                        <button
                            onClick={handleDisable}
                            disabled={disabling}
                            className="btn-secondary text-health-400 border-health-500/30 hover:bg-health-500/10"
                        >
                            {disabling ? "Disabling..." : "Disable 2FA"}
                        </button>
                        {isAdmin && (
                            <p className="text-caption text-text-muted">
                                Admin users are required to have 2FA enabled.
                            </p>
                        )}
                    </div>
                ) : (
                    <div className="space-y-3">
                        <p className="text-body-sm text-text-secondary">
                            Protect your account by requiring a verification code from an authenticator app when signing in.
                        </p>
                        <button
                            onClick={() => { setShowSetup(true); setError(""); setSuccess(""); }}
                            className="btn-primary"
                        >
                            Enable 2FA
                        </button>
                    </div>
                )}
            </div>

            {showSetup && (
                <TotpSetupModal
                    onClose={() => setShowSetup(false)}
                    onEnabled={() => {
                        setShowSetup(false);
                        setSuccess("2FA enabled successfully");
                        onStatusChange();
                    }}
                />
            )}
        </>
    );
}
